import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { BRAND } from "@/constants/brand";

const LINKS = [
  { label: "Inicio", hash: "#inicio", testid: "nav-link-inicio" },
  { label: "Sobre Nosotros", hash: "#nosotros", testid: "nav-link-nosotros" },
  { label: "Servicios", hash: "#servicios", testid: "nav-link-servicios" },
  { label: "Aparatos", page: "/aparatos", testid: "nav-link-aparatos" },
  { label: "Garantías", hash: "#garantias", testid: "nav-link-garantias" },
  { label: "Preguntas", hash: "#faq", testid: "nav-link-faq" },
];

const NavLink = ({ link, onClick }) => {
  const { pathname } = useLocation();
  const cls =
    "text-sm font-medium text-slate-600 transition-colors duration-300 hover:text-ocean-600";
  if (link.page) {
    return (
      <Link to={link.page} data-testid={link.testid} onClick={onClick} className={cls}>
        {link.label}
      </Link>
    );
  }
  if (pathname === "/") {
    return (
      <a href={link.hash} data-testid={link.testid} onClick={onClick} className={cls}>
        {link.label}
      </a>
    );
  }
  return (
    <Link to={`/${link.hash}`} data-testid={link.testid} onClick={onClick} className={cls}>
      {link.label}
    </Link>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3">
          <img
            src={BRAND.logo}
            alt="Logotipo de Clima Oliver"
            className="h-11 w-11 rounded-xl object-cover shadow-sm ring-1 ring-slate-200"
          />
          <span className="font-display text-lg font-bold tracking-tight text-navy">
            Clima <span className="text-ocean-600">Oliver</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop-menu">
          {LINKS.map((link) => (
            <NavLink key={link.hash} link={link} />
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/presupuesto"
            data-testid="nav-quote-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-ember px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-ember/25 transition-all duration-300 hover:bg-ember-700 hover:shadow-ember/40"
          >
            Crear mi presupuesto
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-navy lg:hidden"
          aria-label="Abrir menú"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-4 px-6 py-6">
              {LINKS.map((link) => (
                <NavLink key={link.hash} link={link} onClick={() => setOpen(false)} />
              ))}
              <Link
                to="/presupuesto"
                data-testid="nav-mobile-quote-cta"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-ember px-5 py-3 text-sm font-semibold text-white"
              >
                Crear mi presupuesto
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
